import { ColorTypes } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Control, useForm } from 'react-hook-form';
import { useRecoilValue } from 'recoil';

import { productsCartState } from '../../../../../../../store/batchTicket/atom';
import {
  AssetDefaultOrSelectedTypeMap,
  SubmitUpdateProps,
} from '../../../../../../../store/product/types';

import { DirectTreasuryFormData, getValidationCreateSchema } from './constants';

export const useDirectTreasuryAssetController = (
  offer: AssetDefaultOrSelectedTypeMap['directTreasury'],
  submitUpdate?: (data: SubmitUpdateProps) => Promise<boolean>
) => {
  const directTreasuryOffersSelected = useRecoilValue(productsCartState).directTreasury;

  const itemBasketSelectedIndex = directTreasuryOffersSelected.items.findIndex(
    (item) => item.productId === offer.productId
  );

  const defaultQuantity =
    directTreasuryOffersSelected?.items[itemBasketSelectedIndex]?.orderQuantity ||
    offer.orderQuantity ||
    offer.minimumStepQuantity;
  const defaultValue =
    directTreasuryOffersSelected?.items[itemBasketSelectedIndex]?.orderValue ||
    offer.orderValue ||
    offer.minimumApplication;
  const [isFocused, setIsFocused] = useState(false);
  const [prevValue, setPrevValue] = useState(0);
  const [showMessageAdjustUnitPrice, setShowMessageAdjustUnitPrice] = useState(false);
  const [valueApplied, setValueApplied] = useState(defaultValue);
  const [amountItem, setAmountItem] = useState(defaultQuantity);

  const valueAppliedFormatted = Utils.Mask.Money(valueApplied);
  const minValueFormatted = Utils.Mask.Money(offer.minimumApplication);

  const tableForm = useForm<DirectTreasuryFormData>({
    mode: 'all',
    reValidateMode: 'onBlur',
    defaultValues: {
      priceItem: minValueFormatted,
    },
    resolver: zodResolver(
      getValidationCreateSchema(
        offer.minimumApplication || 0,
        offer.maximumInvestment || Number.MAX_SAFE_INTEGER
      )
    ),
  });

  const isAbleToChange = (value: number) => {
    return !!(
      offer.minimumApplication &&
      offer.maximumInvestment &&
      prevValue !== value &&
      value >= offer.minimumApplication &&
      value <= offer.maximumInvestment
    );
  };

  const getCorrectPriceItem = () => {
    const currentValue = tableForm.getValues('priceItem');
    const currentValueUnformatted = Utils.Unmask.Money(currentValue);

    const multiple = Number(offer.mininumPurchasePriceAdjusted);
    const isMultipleBalance = currentValueUnformatted % multiple === 0;

    const quotient = parseInt((currentValueUnformatted / multiple).toFixed(0), 10);
    const multipliedValue = quotient * parseFloat(multiple.toFixed(2));
    const priceItem = parseFloat(multipliedValue.toFixed(2));

    return { priceItem, isMultipleBalance, quotient };
  };

  const getPriceItemOrMinimumApplication = () => {
    const { priceItem } = getCorrectPriceItem();
    const minimumApplication = offer.minimumApplication || 0;
    return priceItem > minimumApplication ? priceItem : minimumApplication;
  };

  const handleSubmit = async () => {
    const priceItem = getPriceItemOrMinimumApplication();

    const updateFormValues = (amount: number) => {
      tableForm.setValue('priceItem', Utils.Mask.Money(amount));
      setValueApplied(amount);
    };

    if (submitUpdate) {
      const response = await submitUpdate({ amount: priceItem, price: amountItem || 0 });
      if (response) {
        updateFormValues(priceItem);
      } else {
        updateFormValues(prevValue);
      }
    } else {
      updateFormValues(priceItem);
    }

    setIsFocused(false);
  };

  const handleChangeAmountItem = async (value: number) => {
    const multiple = Number((offer.purchasePrice || 0).toFixed(2));
    const multipliedValue = value * parseFloat(multiple.toFixed(2));
    const priceItem = parseFloat(multipliedValue.toFixed(2));

    const updateFormValues = () => {
      setAmountItem(value);
      tableForm.setValue('priceItem', Utils.Mask.Money(priceItem));
      setValueApplied(priceItem);
    };

    if (submitUpdate) {
      const response = await submitUpdate({ amount: priceItem, price: value });
      if (response) {
        updateFormValues();
      }
    } else {
      updateFormValues();
    }
  };

  const handleInputBlur = async () => {
    const currentValue = tableForm.getValues('priceItem');

    if (currentValue !== minValueFormatted) {
      const { isMultipleBalance, quotient, priceItem } = getCorrectPriceItem();
      const priceItemFormatted = Utils.Mask.Money(priceItem);

      const updateFormValues = () => {
        tableForm.setValue('priceItem', priceItemFormatted);
        setValueApplied(priceItem);
        setPrevValue(priceItem);
        setAmountItem(quotient / 100);
      };

      if (isAbleToChange(priceItem)) {
        if (submitUpdate) {
          const response = await submitUpdate({ amount: priceItem, price: amountItem || 0 });
          if (response) {
            updateFormValues();
          }
        } else {
          updateFormValues();
        }
        if (!isMultipleBalance) {
          setShowMessageAdjustUnitPrice(true);
        }
      }
    }
    if (isFocused) {
      setIsFocused(false);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    setShowMessageAdjustUnitPrice(false);
    setPrevValue(offer.minimumApplication || 0);
    if (tableForm.formState.errors?.priceItem) {
      tableForm.resetField('priceItem');
    }
  };

  const handleInputClear = () => {
    setIsFocused(false);
    tableForm.resetField('priceItem');
    tableForm.clearErrors('priceItem');
  };

  const productsDetailText = 'Veja mais detalhes\nsobre este produto';

  const helpTextWhenIsFocused = showMessageAdjustUnitPrice
    ? 'Ajustado por preço unitário'
    : `Mínimo: ${Utils.Mask.Money(offer.minimumApplication)}`;

  const messageAdjustUnitPriceColor = {
    color: (isFocused ? 'brand.tertiary.principal' : 'neutral.light') as ColorTypes.Option,
  };

  return {
    handleInputFocus,
    handleInputClear,
    isFocused,
    handleInputBlur,
    handleChangeAmountItem,
    handleSubmit: tableForm.handleSubmit(handleSubmit),
    control: tableForm.control as unknown as Control,
    showMessageAdjustUnitPrice,
    helpTextProps: messageAdjustUnitPriceColor,
    helpText: helpTextWhenIsFocused,
    hasError: tableForm.formState.errors?.priceItem,
    currentValue: valueApplied,
    currentValueFormatted: valueAppliedFormatted,
    amountItem,
    productsDetailText,
  };
};

export type DirectTreasuryController = ReturnType<typeof useDirectTreasuryAssetController>;
